import { useState } from 'react';
import { Archive, Eye, Film, Image, RotateCcw, Trash2 } from 'lucide-react';

import { useTranslation } from '../../../context/LanguageContext';
import { ConfirmDialog } from '../../core/ConfirmDialog';
import { Badge } from '../../core/Badge';
import { formatBytes } from '../../../utils/format';
import type { MediaAsset } from '../../../hooks/useMediaLibrary';

interface MediaCardProps {
  asset: MediaAsset;
  isAdmin: boolean;
  onView: (asset: MediaAsset) => void;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
  onDelete: (id: string) => void;
}

export function MediaCard({
  asset,
  isAdmin,
  onView,
  onArchive,
  onRestore,
  onDelete,
}: Readonly<MediaCardProps>) {
  const { t } = useTranslation();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isArchived = asset.status === 'archived';

  const durationLabel =
    asset.durationMs != null
      ? t('media.duration', { s: String(Math.round(asset.durationMs / 1000)) })
      : null;

  return (
    <>
      <div
        className={`group relative rounded-xl border bg-slate-800 overflow-hidden transition-colors ${
          isArchived
            ? 'border-slate-700/50 opacity-60'
            : 'border-slate-700 hover:border-violet-500/40'
        }`}
      >
        {/* Thumbnail */}
        <button
          className="relative block w-full aspect-video bg-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          onClick={() => onView(asset)}
          aria-label={t('media.view')}
        >
          {asset.thumbnailUrl ? (
            <img
              src={asset.thumbnailUrl}
              alt={asset.altText ?? asset.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Film className="h-8 w-8 text-slate-600" />
            </div>
          )}

          {/* Type badge */}
          <span className="absolute top-2 left-2">
            {asset.type === 'video' ? (
              <span className="flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
                <Film className="h-3 w-3" /> {durationLabel ?? 'video'}
              </span>
            ) : (
              <span className="flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
                <Image className="h-3 w-3" />
                {asset.width && asset.height
                  ? t('media.dimensions', { w: String(asset.width), h: String(asset.height) })
                  : 'image'}
              </span>
            )}
          </span>

          {/* Archived overlay */}
          {isArchived && (
            <span className="absolute top-2 right-2">
              <Badge variant="warning">Archived</Badge>
            </span>
          )}

          {/* Play overlay for video */}
          {asset.type === 'video' && !isArchived && (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                <Film className="h-5 w-5 text-white" />
              </div>
            </div>
          )}

          {/* View overlay for image */}
          {asset.type === 'image' && !isArchived && (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                <Eye className="h-5 w-5 text-white" />
              </div>
            </div>
          )}
        </button>

        {/* Footer */}
        <div className="px-3 py-2.5">
          <p className="text-xs font-semibold text-white truncate" title={asset.name}>
            {asset.name}
          </p>
          <div className="flex items-center justify-between mt-0.5">
            <p className="text-[10px] text-slate-500">{formatBytes(asset.sizeBytes)}</p>

            {/* Actions */}
            <div className="relative">
              <button
                onClick={() => setMenuOpen((o) => !o)}
                onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
                className="text-slate-500 hover:text-white transition-colors px-1"
                aria-label="Options"
              >
                ···
              </button>
              {menuOpen && (
                <div className="absolute bottom-6 right-0 z-10 w-44 rounded-xl border border-slate-700 bg-slate-900 shadow-xl py-1">
                  {!isArchived && (
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onView(asset);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
                    >
                      <Eye className="h-3.5 w-3.5" /> {t('media.view')}
                    </button>
                  )}
                  {!isArchived && (
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        setConfirmArchive(true);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
                    >
                      <Archive className="h-3.5 w-3.5" /> {t('media.archive')}
                    </button>
                  )}
                  {isArchived && (
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onRestore(asset.id);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> {t('media.restore')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmDelete(true);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-xs text-rose-400 hover:bg-slate-800"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {t('media.delete')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Admin: show owner */}
          {isAdmin && asset.ownerEmail && (
            <p className="text-[10px] text-slate-600 truncate mt-0.5" title={asset.ownerEmail}>
              {t('media.ownerLabel', { email: asset.ownerEmail })}
            </p>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmArchive}
        onOpenChange={setConfirmArchive}
        title={t('media.archiveTitle')}
        description={t('media.archiveBody')}
        confirmLabel={t('media.archive')}
        onConfirm={() => {
          onArchive(asset.id);
          setConfirmArchive(false);
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('media.deleteTitle')}
        description={t('media.deleteBody')}
        confirmLabel={t('media.delete')}
        variant="destructive"
        onConfirm={() => {
          onDelete(asset.id);
          setConfirmDelete(false);
        }}
      />
    </>
  );
}
